import type { components } from './api.generated';
import { randomId } from './randomId';

interface Pending {
  readonly target: string;
  readonly resolve: () => void;
  readonly reject: (reason: Error) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

export class MuteSender {
  readonly muted = new Set<string>();
  private readonly pending = new Map<string, Pending>();

  constructor(private readonly connection: () => WebSocket | null) {}

  set(target: string, muted: boolean): Promise<void> {
    const socket = this.connection();
    if (!socket || socket.readyState !== WebSocket.OPEN)
      return Promise.reject(new Error('The shared city is not connected.'));
    if ([...this.pending.values()].some((value) => value.target === target))
      return Promise.reject(new Error('Wait for the city to confirm this change.'));
    const request_id = randomId();
    const request: components['schemas']['MuteRequest'] = {
      type: 'mute',
      request_id,
      target,
      muted,
    };
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(request_id);
        reject(new Error('The city did not confirm this change.'));
      }, 5000);
      this.pending.set(request_id, { target, resolve, reject, timer });
      socket.send(JSON.stringify(request));
    });
  }

  receive(result: components['schemas']['MuteResult']): void {
    const pending = this.pending.get(result.request_id);
    if (!pending || pending.target !== result.target) return;
    clearTimeout(pending.timer);
    this.pending.delete(result.request_id);
    if (result.status !== 'applied') {
      pending.reject(new Error('This visitor has left the city.'));
      return;
    }
    if (result.muted) this.muted.add(result.target);
    else this.muted.delete(result.target);
    pending.resolve();
  }

  fail(): void {
    for (const value of this.pending.values()) {
      clearTimeout(value.timer);
      value.reject(new Error('The city did not confirm this change.'));
    }
    this.pending.clear();
  }

  reset(): void {
    this.fail();
    this.muted.clear();
  }
}
